import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Box,
    Link,
    Typography,
    Button,
} from '@mui/material';
import DialogTitleWithClose from '../components/DialogTitleWithClose';
import * as constants from '../common/constants';
import { getLabelFromJspVariables } from '../common/JspUtil';

const PASSKEY_HELP_BASE = constants.RESOURCES_BASE + 'passkey-help/';
const CHROME_ICON = constants.IMG_BASE + 'chrome.svg';
const EDGE_ICON = constants.IMG_BASE + 'edge.svg';
const BROWSER_ICON_SIZE = 24;

const TAB_MAC = 0;
const TAB_WINDOWS = 1;

const windowsBrowsers = [
    {
        browser: 'Chrome',
        links: [
            { label: 'Yubikey', file: 'Passkey-Yubikey-Windows-chrome.pdf' },
            { label: 'Fingerprint', file: 'Passkey-Fingerprint-Windows-chrome.pdf' }
        ]
    },
    {
        browser: 'Edge',
        links: [
            { label: 'Yubikey', file: 'Passkey-Yubikey-Windows-edge.pdf' },
            { label: 'Fingerprint', file: 'Passkey-Fingerprint-Windows-edge.pdf' }
        ]
    }
];

const macBrowsers = [
    {
        browser: 'Chrome',
        links: [
            { label: 'Yubikey', file: 'Passkey-Yubikey-Mac-Chrome.pdf' },
            { label: 'Fingerprint', file: 'Passkey-Fingerprint-Mac-Chrome.pdf' }
        ]
    },
    {
        browser: 'Edge',
        links: [
            { label: 'Yubikey', file: 'Passkey-Yubikey-Mac-Edge.pdf' },
            { label: 'Fingerprint', file: 'Passkey-Fingerprint-Mac-Edge.pdf' }
        ]
    }
];

function detectDefaultTab() {
    const userAgent = navigator.userAgent || '';
    if (userAgent.indexOf('Mac') !== -1) {
        return TAB_MAC;
    }
    return TAB_WINDOWS;
}

const BROWSER_ICONS = {
    Chrome: CHROME_ICON,
    Edge: EDGE_ICON
};

const tabsSx = {
    borderBottom: '1px solid #ccc',
    backgroundColor: '#f5f5f5',
    minHeight: 36,
    '& .MuiTabs-indicator': {
        backgroundColor: '#000',
    },
};

const tabSx = {
    textTransform: 'uppercase',
    fontWeight: 600,
    fontSize: '13px',
    minHeight: 36,
    minWidth: 100,
    color: '#666',
    '&.Mui-selected': {
        color: '#fff',
        backgroundColor: '#555',
    },
};

function TabPanel(props) {
    const children = props.children;
    const value = props.value;
    const index = props.index;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={'passkey-tabpanel-' + index}
            data-automation-id={'passkey_tabpanel_' + index}
            aria-labelledby={'passkey-tab-' + index}
        >
            {value === index && (
                <Box sx={{ pt: 2 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: 'passkey-tab-' + index,
        'aria-controls': 'passkey-tabpanel-' + index
    };
}

function BrowserSection(props) {
    const browser = props.browser;
    const links = props.links;
    const folder = props.folder;
    const iconSrc = BROWSER_ICONS[browser];

    return (
        <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <img src={iconSrc} alt="" width={BROWSER_ICON_SIZE} height={BROWSER_ICON_SIZE} aria-hidden="true" />
                <Typography variant="subtitle1" component="h3" style={{ fontWeight: 600, marginLeft: '8px', color: '#333' }}>
                    {browser}
                </Typography>
            </div>
            <ul style={{ listStyle: 'none', padding: '0 0 0 32px', margin: 0 }}>
                {links.map(function (item, i) {
                    const href = PASSKEY_HELP_BASE + folder + '/' + item.file;
                    return (
                        <li key={i} style={{ padding: '3px 0' }}>
                            <Link
                                href={href}
                                download={item.file}
                                underline="always"
                                style={{ color: '#333', fontSize: '14px' }}
                                aria-label={'Download ' + item.label + ' passkey guide for ' + browser + ' (PDF)'}
                                data-automation-id={`passkey_help_${folder}_${browser.toLowerCase()}_${item.label.toLowerCase()}_link`}
                            >
                                {item.label}
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function BrowserList(props) {
    const browsers = props.browsers;
    const folder = props.folder;

    return (
        <div>
            {browsers.map(function (section, i) {
                return (
                    <BrowserSection
                        key={i}
                        browser={section.browser}
                        links={section.links}
                        folder={folder}
                    />
                );
            })}
        </div>
    );
}

function PasskeyHelpDialog(props) {
    const open = props.open;
    const onClose = props.onClose;

    const defaultTab = detectDefaultTab();
    const tabState = useState(defaultTab);
    const activeTab = tabState[0];
    const setActiveTab = tabState[1];

    const handleTabChange = function (_event, newValue) {
        setActiveTab(newValue);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            aria-labelledby="passkey-help-dialog-title"
            aria-describedby="passkey-help-dialog-description"
        >
            <DialogTitleWithClose
                id="passkey-help-dialog-title"
                dataAutomationId="passkey_help_dialog_title"
                onClose={onClose}
                sx={{ padding: '12px 16px', borderBottom: '1px solid #ddd' }}
            >
                <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600, color: '#333' }}>
                    {getLabelFromJspVariables('title_passkey_help')}
                </Typography>
            </DialogTitleWithClose>
            <DialogContent id="passkey-help-dialog-description" style={{ padding: '0 24px 16px' }}>
                <Box style={{ margin: '0 -24px', marginBottom: '8px' }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        aria-label="Passkey setup guides by operating system"
                        sx={tabsSx}
                        data-automation-id="passkey_help_tabs"
                    >
                        <Tab label="MAC" sx={tabSx} {...a11yProps(TAB_MAC)} data-automation-id="passkey_help_tab_mac" />
                        <Tab label="Windows" sx={tabSx} {...a11yProps(TAB_WINDOWS)} data-automation-id="passkey_help_tab_windows" />
                    </Tabs>
                </Box>
                <TabPanel value={activeTab} index={TAB_MAC}>
                    <BrowserList browsers={macBrowsers} folder="mac" />
                </TabPanel>
                <TabPanel value={activeTab} index={TAB_WINDOWS}>
                    <BrowserList browsers={windowsBrowsers} folder="windows" />
                </TabPanel>
            </DialogContent>
            <DialogActions style={{ borderTop: '1px solid #ddd', padding: '12px 16px' }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    data-automation-id="passkey_help_close_btn"
                    style={{
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        padding: '6px 24px',
                        borderRadius: '2px'
                    }}
                >
                    {getLabelFromJspVariables('action_close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default PasskeyHelpDialog;
